// ReservationFormModal.jsx
// Enterprise-grade backoffice Reservation Wizard (Production Ready) - World-Class UX Enhanced
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import moment from "moment";
import "moment-timezone";
import axios from "axios";
import debounce from "lodash/debounce";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ReservationFormModal = ({
  visible,
  initial,
  tables = [],
  orgHours,
  reservations = [],
  onSubmit,
  onClose,
}) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  // Safe hours fallback
  const SAFE_HOURS = useMemo(() => ({
    openTime: orgHours?.openTime || "11:00",
    closeTime: orgHours?.closeTime || "23:00",
    slotMinutes: Number(orgHours?.slotMinutes) || 15,
    timezone: orgHours?.timezone || moment.tz.guess(),
  }), [orgHours]);

  const tz = SAFE_HOURS.timezone;
  const slotMinutes = Math.max(Number(SAFE_HOURS.slotMinutes) || 15, 1);

  // State
  const [step, setStep] = useState(1);
  const [showAllTimes, setShowAllTimes] = useState(false);
  const [displayedWeekStart, setDisplayedWeekStart] = useState(() => moment().tz(tz).clone().startOf('isoWeek'));
  const [formValues, setFormValues] = useState({
    customerName: "",
    phone: "",
    phoneCountryCode: "",
    phoneNational: "",
    email: "",
    partySize: "",
    date: moment().tz(tz).format("YYYY-MM-DD"),
    time: "",
    duration: "60",
    tableId: "unassigned",
    source: "Phone",
    status: "Confirmed",
    occasion: "",
    notes: "",
  });

  const [errors, setErrors] = useState({});
  const [collisionError, setCollisionError] = useState(null);
  const [selectedGuestOption, setSelectedGuestOption] = useState(null);

  // Helpers
  const setField = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: null }));
    setCollisionError(null);
  };

  const handleInputChange = (e) => setField(e.target.name, e.target.value);
  const handleSelectChange = (option, field) => setField(field, option?.value ?? "");

  const to12h = (hhmm) => moment(hhmm, "HH:mm").format("h:mm a");

  // Phone input handler
  const handlePhoneChange = (value, countryData) => {
    const dialCode = countryData?.dialCode || "";
    const full = value.startsWith("+") ? value : `+${value}`;
    const national = dialCode && value.startsWith(dialCode)
      ? value.slice(dialCode.length)
      : value;
    setFormValues(prev => ({
      ...prev,
      phone: full,
      phoneCountryCode: dialCode ? `+${dialCode}` : "",
      phoneNational: national,
    }));
    setErrors(prev => ({ ...prev, phone: null }));
  };

  // ──────────────────────────────────────────────
  // Options
  // ──────────────────────────────────────────────
  const partySizeOptions = useMemo(() =>
    Array.from({ length: 20 }, (_, i) => ({
      value: String(i + 1),
      label: String(i + 1),
    })), []);

  const durationOptions = useMemo(() => [
    { value: "30", label: "30 min" },
    { value: "60", label: "60 min" },
    { value: "90", label: "90 min" },
    { value: "120", label: "120 min" },
  ], []);

  const tableOptions = useMemo(() => [
    { value: "unassigned", label: "Unassigned" },
    ...tables.map(t => ({
      value: String(t.id),
      label: `${t.label} (Capacity: ${t.capacity})`,
    })),
  ], [tables]);

  const sourceOptions = useMemo(() => [
    { value: "WalkIn", label: "Walk-In" },
    { value: "Website", label: "Website" },
    { value: "Phone", label: "Phone" },
    { value: "Partner", label: "Partner" },
  ], []);

  const statusOptions = useMemo(() => [
    { value: "Confirmed", label: "Confirmed" },
    { value: "CheckedIn", label: "Checked In" },
    { value: "Cancelled", label: "Cancelled" },
    { value: "NoShow", label: "No Show" },
  ], []);

  const occasionOptions = useMemo(() => [
    { value: "", label: "Select an occasion (optional)" },
    { value: "Birthday", label: "Birthday" },
    { value: "Anniversary", label: "Anniversary" },
    { value: "DateNight", label: "Date night" },
    { value: "BusinessMeal", label: "Business Meal" },
  ], []);

  const portalStyles = useMemo(() => ({
    menuPortal: base => ({ ...base, zIndex: 2147483647 }),
  }), []);

  const getValue = (options, current) =>
    options.find(o => String(o.value) === String(current)) || null;

  // ──────────────────────────────────────────────
  // Date & Time logic
  // ──────────────────────────────────────────────
  const todayMoment = useMemo(() => moment().tz(tz), [tz]);
  const selectedDateMoment = useMemo(() =>
    moment.tz(formValues.date || moment().tz(tz).format("YYYY-MM-DD"), "YYYY-MM-DD", tz),
  [formValues.date, tz]);

  // Update displayed week when selected date changes
  useEffect(() => {
    const weekStart = selectedDateMoment.clone().startOf('isoWeek');
    if (!weekStart.isSame(displayedWeekStart, 'day')) {
      setDisplayedWeekStart(weekStart);
    }
  }, [selectedDateMoment, displayedWeekStart]);

  const currentWeekDays = useMemo(() => {
    const days = [];
    let current = displayedWeekStart.clone();
    for (let i = 0; i < 7; i++) {
      const isToday = current.isSame(todayMoment, 'day');
      const isSelected = current.isSame(selectedDateMoment, 'day');
      const isPast = current.isBefore(todayMoment, 'day');
      days.push({
        moment: current.clone(),
        dayName: current.format('ddd').toUpperCase(),
        dateNum: current.format('D'),
        month: current.format('MMM'),
        isToday,
        isSelected,
        isPast,
      });
      current.add(1, 'day');
    }
    return days;
  }, [displayedWeekStart, todayMoment, selectedDateMoment]);

  const prevWeek = () => {
    const newStart = displayedWeekStart.clone().subtract(7, 'days');
    if (!newStart.isBefore(todayMoment.startOf('isoWeek'))) {
      setDisplayedWeekStart(newStart);
    }
  };

  const nextWeek = () => setDisplayedWeekStart(d => d.clone().add(7, 'days'));

  const selectToday = () => {
    setField("date", todayMoment.format("YYYY-MM-DD"));
    setField("time", "");
  };

  const handleDateChange = (date) => {
    if (date) {
      const newDate = moment(date).tz(tz).format("YYYY-MM-DD");
      setField("date", newDate);
      setField("time", "");
    }
  };

  const openDT = useMemo(() =>
    moment.tz(`${selectedDateMoment.format("YYYY-MM-DD")} ${SAFE_HOURS.openTime}`, "YYYY-MM-DD HH:mm", tz),
  [selectedDateMoment, SAFE_HOURS.openTime, tz]);

  const closeDT = useMemo(() => {
    const base = moment.tz(`${selectedDateMoment.format("YYYY-MM-DD")} ${SAFE_HOURS.closeTime}`, "YYYY-MM-DD HH:mm", tz);
    return base.isBefore(openDT) ? base.add(1, "day") : base;
  }, [selectedDateMoment, SAFE_HOURS.closeTime, tz, openDT]);

  const timeSlots = useMemo(() => {
    const slots = [];
    let cur = openDT.clone();
    let guard = 0;
    while (cur.isSameOrBefore(closeDT) && guard < 1440) {
      slots.push(cur.format("HH:mm"));
      cur.add(slotMinutes, "minutes");
      guard += slotMinutes;
    }
    if (slots.length === 0) {
      const fbStart = moment.tz(`${selectedDateMoment.format("YYYY-MM-DD")} 11:00`, "YYYY-MM-DD HH:mm", tz);
      const fbEnd = moment.tz(`${selectedDateMoment.format("YYYY-MM-DD")} 23:00`, "YYYY-MM-DD HH:mm", tz);
      let fbCur = fbStart.clone();
      let g = 0;
      while (fbCur.isSameOrBefore(fbEnd) && g < 1440) {
        slots.push(fbCur.format("HH:mm"));
        fbCur.add(15, "minutes");
        g += 15;
      }
    }
    return slots;
  }, [openDT, closeDT, slotMinutes, selectedDateMoment, tz]);

  const slotAvailability = useMemo(() => {
    const map = {};
    const durationMin = Number(formValues.duration) || 60;
    const isToday = selectedDateMoment.isSame(todayMoment, "day");
    timeSlots.forEach(slot => {
      const start = moment.tz(`${formValues.date} ${slot}`, "YYYY-MM-DD HH:mm", tz);
      const end = start.clone().add(durationMin, "minutes");
      const withinHours = !start.isBefore(openDT) && !end.isAfter(closeDT);
      const isPastTime = isToday && start.isBefore(todayMoment);
      let collides = false;
      if (formValues.tableId !== "unassigned") {
        collides = reservations.some(res => {
          if (res.id === initial?.id) return false;
          if (res.tableId !== formValues.tableId) return false;
          return start.isBefore(moment(res.end)) && moment(res.start).isBefore(end);
        });
      }
      map[slot] = {
        withinHours,
        isPastTime,
        collides,
        disabled: !withinHours || isPastTime || collides,
      };
    });
    return map;
  }, [
    timeSlots, formValues.date, formValues.duration, formValues.tableId,
    reservations, initial?.id, tz, openDT, closeDT, selectedDateMoment, todayMoment
  ]);

  const visibleSlots = useMemo(() =>
    timeSlots.filter(slot => !slotAvailability[slot]?.isPastTime),
  [timeSlots, slotAvailability]);

  const timeSlotsToRender = useMemo(() =>
    showAllTimes ? visibleSlots : visibleSlots.slice(0, 8),
  [visibleSlots, showAllTimes]);

  // Auto-select first visible slot when appropriate
  useEffect(() => {
    if (!formValues.time && visibleSlots.length > 0) {
      setField("time", visibleSlots[0]);
    }
  }, [visibleSlots, formValues.time, setField]);

  // Clear invalid time
  useEffect(() => {
    if (formValues.time && slotAvailability[formValues.time]?.isPastTime) {
      setField("time", "");
    }
  }, [slotAvailability, formValues.time, setField]);

  // ──────────────────────────────────────────────
  // Guest Search
  // ──────────────────────────────────────────────
  const loadGuestOptions = useCallback(debounce(async (inputValue, callback) => {
    if (!inputValue?.trim()) return callback([]);
    try {
      const response = await axios.get(`${apiUrl}/pos/reservations/search`, {
        params: { name: inputValue, phone: inputValue, email: inputValue },
        headers: { Authorization: `Bearer ${token}` },
      });
      const options = (response.data.guests || []).map(guest => ({
        value: guest,
        label: `${guest.full_name || guest.name || "—"} (${guest.phone_no || guest.phone || "—"})`
      }));
      callback(options);
    } catch (err) {
      toast.error("Failed to search guests");
      callback([]);
    }
  }, 500), [apiUrl, token]);

  const handleGuestSelect = (selectedOption) => {
    setSelectedGuestOption(selectedOption);
    if (selectedOption) {
      const guest = selectedOption.value;
      setFormValues(prev => ({
        ...prev,
        customerName: guest.full_name || guest.name || "",
        phone: guest.phone_no || guest.phone || "",
        email: guest.email || "",
      }));
    } else {
      setFormValues(prev => ({
        ...prev,
        customerName: "",
        phone: "",
        phoneCountryCode: "",
        phoneNational: "",
        email: "",
      }));
    }
  };

  // ──────────────────────────────────────────────
  // Initialization
  // ──────────────────────────────────────────────
  useEffect(() => {
    if (!visible) return;
    setErrors({});
    setCollisionError(null);
    setStep(1);
    setShowAllTimes(false);
    const initDate = initial ? moment(initial.start).tz(tz).format("YYYY-MM-DD") : moment().tz(tz).format("YYYY-MM-DD");
    setDisplayedWeekStart(moment.tz(initDate, tz).startOf('isoWeek'));
    if (initial) {
      const start = moment(initial.start).tz(tz);
      const end = moment(initial.end).tz(tz);
      const durationMin = Math.round(end.diff(start, "minutes"));
      const normalizedDuration = ["30","60","90","120"].includes(String(durationMin))
        ? String(durationMin)
        : "60";
      setFormValues({
        customerName: initial.customerName || "",
        phone: initial.phone || "",
        phoneCountryCode: initial.phoneCountryCode || "",
        phoneNational: initial.phoneNational || "",
        email: initial.email || "",
        partySize: String(initial.partySize || ""),
        date: start.format("YYYY-MM-DD"),
        time: start.format("HH:mm"),
        duration: normalizedDuration,
        tableId: String(initial.tableId || "unassigned"),
        source: initial.source || "Phone",
        status: initial.status || "Confirmed",
        occasion: initial.occasion || "",
        notes: initial.notes || "",
      });
    } else {
      setFormValues({
        customerName: "",
        phone: "",
        phoneCountryCode: "",
        phoneNational: "",
        email: "",
        partySize: "",
        date: moment().tz(tz).format("YYYY-MM-DD"),
        time: "",
        duration: "60",
        tableId: "unassigned",
        source: "Phone",
        status: "Confirmed",
        occasion: "",
        notes: "",
      });
    }
  }, [visible, initial, tz]);

  useEffect(() => {
    if (!visible) return undefined;
    document.body.classList.add("ns-res-form-open");
    return () => document.body.classList.remove("ns-res-form-open");
  }, [visible]);

  // ──────────────────────────────────────────────
  // Validation & Submit
  // ──────────────────────────────────────────────
  const validateStep1 = () => {
    const errs = {};
    if (!formValues.partySize) errs.partySize = "Party size is required";
    if (!formValues.date) errs.date = "Date is required";
    if (!formValues.time) errs.time = "Time slot is required";
    if (!formValues.duration || !durationOptions.some(o => o.value === formValues.duration))
      errs.duration = "Please select valid duration";
    if (formValues.time && slotAvailability[formValues.time]?.disabled) {
      errs.time = "Selected time is not available";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep2 = () => {
    const errs = {};
    if (!formValues.customerName?.trim()) errs.customerName = "Name is required";
    if (!formValues.phone?.trim()) errs.phone = "Phone is required";
    if (!formValues.source) errs.source = "Source is required";
    if (!formValues.status) errs.status = "Status is required";
    setErrors(prev => ({ ...prev, ...errs }));
    return Object.keys(errs).length === 0;
  };

  const buildReservationPayload = (forceUnassigned = false) => {
    const start = moment.tz(`${formValues.date} ${formValues.time}`, "YYYY-MM-DD HH:mm", tz);
    const end = start.clone().add(Number(formValues.duration), "minutes");
    return {
      id: initial?.id,
      customerName: formValues.customerName.trim(),
      phone: formValues.phone.trim(),
      phoneCountryCode: formValues.phoneCountryCode.trim(),
      phoneNational: formValues.phoneNational.trim(),
      email: (formValues.email || "").trim(),
      partySize: Number(formValues.partySize),
      start: start.toISOString(),
      end: end.toISOString(),
      tableId: forceUnassigned ? "unassigned" : formValues.tableId,
      source: formValues.source,
      status: formValues.status,
      occasion: formValues.occasion || "",
      notes: (formValues.notes || "").trim(),
    };
  };

  const doFinalSubmit = () => {
    if (!validateStep1()) {
      setStep(1);
      return;
    }
    if (!validateStep2()) return;
    if (formValues.tableId !== "unassigned" && slotAvailability[formValues.time]?.collides) {
      setCollisionError("This table is already booked at selected time. Save as unassigned?");
      return;
    }
    onSubmit({ reservation: buildReservationPayload(false) });
    setErrors({});
    setCollisionError(null);
    setStep(1);
    onClose();
  };

  const handleSaveUnassigned = () => {
    onSubmit({ reservation: buildReservationPayload(true) });
    setCollisionError(null);
    setStep(1);
    onClose();
  };

const handleClose = () => {
  setCollisionError(null);
  setErrors({});
  setStep(1);
  setShowAllTimes(false);
  setSelectedGuestOption(null);
  onClose?.();
};


  if (!visible) return null;

  return (
    <div
      id="add_reservation"
      className="ns-res-form-root"
      role="dialog"
      aria-modal="true"
    >
      <ToastContainer />
      <button
        type="button"
        className="ns-res-form-backdrop"
        aria-label="Close reservation form"
        onClick={handleClose}
      />

      <div className="ns-res-form-drawer">
        <div className="ns-res-form-content">
          {/* Header */}
          <div className="ns-res-form-header">
            <div className="ns-res-form-title-wrap">
              <span className="ns-res-form-icon">{step}</span>
              <div>
                <div className="ns-res-form-eyebrow">Reservation Wizard</div>
                <h4>{initial ? "Edit Reservation" : "New Reservation"}</h4>
                <p>Step {step} of 2 · {step === 1 ? "Select date, table, and time" : "Guest details and confirmation"}</p>
              </div>
            </div>
            <button type="button" className="ns-res-form-close" onClick={handleClose} aria-label="Close">
              ×
            </button>
          </div>
          {/* Body */}
          <div className="ns-res-form-body">
            {collisionError && (
              <div className="alert alert-danger d-flex align-items-center justify-content-between mb-3" role="alert">
                <div>{collisionError}</div>
                <div>
                  <button type="button" className="btn btn-secondary btn-sm me-2" onClick={() => setCollisionError(null)}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary btn-sm" onClick={handleSaveUnassigned}>
                    Save as Unassigned
                  </button>
                </div>
              </div>
            )}
            {step === 1 && (
              <>
                {/* Party size + Date */}
                <div className="row g-3 mb-4 ns-res-form-card">
                  <div className="col-md-4">
                    <label className="form-label fw-bold">Party size</label>
                    <Select
                      options={partySizeOptions}
                      value={getValue(partySizeOptions, formValues.partySize)}
                      onChange={opt => handleSelectChange(opt, "partySize")}
                      placeholder="Select"
                      menuPortalTarget={document.body}
                      styles={portalStyles}
                      classNamePrefix="react-select"
                    />
                    {errors.partySize && <small className="text-danger">{errors.partySize}</small>}
                  </div>
                  <div className="col-md-8">
                    <label className="form-label fw-bold">Date</label>
                    <DatePicker
                      selected={moment(formValues.date, "YYYY-MM-DD").toDate()}
                      onChange={handleDateChange}
                      minDate={todayMoment.toDate()}
                      dateFormat="MM/dd/yyyy"
                      className="form-control"
                      wrapperClassName="w-100"
                    />
                    {errors.date && <small className="text-danger">{errors.date}</small>}
                  </div>
                </div>
                {/* Horizontal Day Selector Layer */}
                <div className="mb-4 ns-res-form-card">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        className="btn btn-link p-0 text-muted"
                        onClick={prevWeek}
                        disabled={displayedWeekStart.isSameOrBefore(todayMoment.startOf('isoWeek'))}
                      >
                        <i className="bi bi-chevron-left"></i>
                      </button>
                      <span className="text-muted fw-bold">
                        {displayedWeekStart.format("D MMM")} - {displayedWeekStart.clone().add(6, 'days').format("D MMM")}
                      </span>
                      <button
                        type="button"
                        className="btn btn-link p-0 text-muted"
                        onClick={nextWeek}
                      >
                        <i className="bi bi-chevron-right"></i>
                      </button>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-primary rounded-pill"
                      onClick={selectToday}
                    >
                      Today
                    </button>
                  </div>
                  <div className="d-flex gap-2 overflow-auto">
                    {currentWeekDays.map((day) => (
                      <button
                        key={day.moment.format("YYYY-MM-DD")}
                        type="button"
                        className={`day-pill btn flex-grow-1 py-2 px-3 text-center position-relative ${day.isSelected ? "btn-primary" : "btn-light"} ${day.isPast ? "disabled" : ""}`}
                        disabled={day.isPast}
                        onClick={() => {
                          if (!day.isPast) {
                            setField("date", day.moment.format("YYYY-MM-DD"));
                            setField("time", "");
                          }
                        }}
                        style={{ borderRadius: 10, minWidth: 80 }}
                      >
                        {day.isToday && (
                          <span className="badge rounded-pill bg-warning text-dark small position-absolute" style={{ top: '-8px', left: '10px' }}>TODAY</span>
                        )}
                        <div className="small fw-bold">{day.dayName}</div>
                        <div className="fw-bold">{day.dateNum}</div>
                        <div className="small">{day.month}</div>
                      </button>
                    ))}
                  </div>
                </div>
                {/* Duration + Table */}
                <div className="row g-3 mb-4 ns-res-form-card">
                  <div className="col-md-4">
                    <label className="form-label fw-bold">Duration</label>
                    <Select
                      options={durationOptions}
                      value={getValue(durationOptions, formValues.duration)}
                      onChange={opt => handleSelectChange(opt, "duration")}
                      placeholder="Select duration"
                      menuPortalTarget={document.body}
                      styles={portalStyles}
                      classNamePrefix="react-select"
                    />
                    {errors.duration && <small className="text-danger">{errors.duration}</small>}
                  </div>
                  <div className="col-md-8">
                    <label className="form-label fw-bold">Table</label>
                    <Select
                      options={tableOptions}
                      value={getValue(tableOptions, formValues.tableId)}
                      onChange={opt => handleSelectChange(opt, "tableId")}
                      placeholder="Select table"
                      menuPortalTarget={document.body}
                      styles={portalStyles}
                      classNamePrefix="react-select"
                    />
                    <small className="text-muted d-block mt-1">
                      Table already booked? You can save as <strong>Unassigned</strong>.
                    </small>
                  </div>
                </div>
                {/* Time slots */}
                <div className="mb-3 ns-res-form-card">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="fw-bold">AVAILABLE TIME SLOTS</label>
                    {visibleSlots.length > 8 && (
                      <button
                        type="button"
                        className="btn btn-link p-0 text-primary"
                        onClick={() => setShowAllTimes(!showAllTimes)}
                      >
                        {showAllTimes ? "Less times" : "More times"} <i className="bi bi-chevron-right"></i>
                      </button>
                    )}
                  </div>
                  {errors.time && <small className="text-danger">{errors.time}</small>}
                  {timeSlotsToRender.length === 0 ? (
                    <div className="alert alert-warning text-center py-3 mb-0">
                      No available future slots for this date (past times hidden)
                    </div>
                  ) : (
                    <div className="row g-2">
                      {timeSlotsToRender.map(slot => {
                        const meta = slotAvailability[slot] || {};
                        const selected = slot === formValues.time;
                        return (
                          <div key={slot} className="col-3">
                            <button
                              type="button"
                              className={`time-slot w-100 py-3 px-2 text-center rounded-3 position-relative ${selected ? "selected" : ""} ${meta.disabled ? "disabled" : ""}`}
                              disabled={meta.disabled}
                              onClick={() => setField("time", slot)}
                            >
                              <div className="fw-bold">{to12h(slot)}</div>
                              {!meta.collides && !meta.disabled && (
                                <span className="badge bg-success-subtle text-success mt-2 rounded-pill">AVAILABLE</span>
                              )}
                              {meta.collides && (
                                <span className="badge bg-danger-subtle text-danger mt-2 rounded-pill">BOOKED</span>
                              )}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
            {step === 2 && (
              <div className="step-2-content">
                {/* Refined Summary Card */}
                <div
                  className="card border-0 mb-4 rounded-3 shadow-sm"
                  style={{ overflow: 'hidden' }}
                >
                  <div
                    className="px-4 py-3 d-flex justify-content-between align-items-center text-dark"
                    style={{
                      background: 'linear-gradient(to right, #6d28d9, #a855f7)',
                    }}
                  >
                    <h5 className="mb-0 fw-semibold">Reservation Summary</h5>
                    <span
                      className="badge rounded-pill bg-white text-primary px-3 py-1"
                    >
                      {initial ? 'Edit Mode' : 'New Booking'}
                    </span>
                  </div>
                  <div className="p-4 bg-light">
                    <div className="row text-dark">
                      <div className="col-md-3">
                        <div className="text-muted fw-medium mb-1">Date</div>
                        <div className="fw-semibold">{moment(formValues.date).format("dddd, D MMMM YYYY")}</div>
                      </div>
                      <div className="col-md-3">
                        <div className="text-muted fw-medium mb-1">Time</div>
                        <div className="fw-semibold">{to12h(formValues.time)}</div>
                      </div>
                      <div className="col-md-3">
                        <div className="text-muted fw-medium mb-1">Duration</div>
                        <div className="fw-semibold">{formValues.duration} minutes</div>
                      </div>
                      <div className="col-md-3">
                        <div className="text-muted fw-medium mb-1">Party Size</div>
                        <div className="fw-semibold">{formValues.partySize} guests</div>
                      </div>
                    </div>
                    <div className="mt-3 p-3 bg-white rounded">
                      <div className="text-muted fw-medium mb-1">Assigned Table</div>
                      <div className="fw-semibold">
                        {formValues.tableId === "unassigned"
                          ? "Unassigned (Any available table)"
                          : getValue(tableOptions, formValues.tableId)?.label || '—'}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Guest Information Section */}
                <div
                  className="card border-0 shadow-sm mb-4"
                  style={{ borderRadius: '12px' }}
                >
                  <div
                    className="px-4 py-3 border-bottom"
                    style={{ backgroundColor: '#f8fafc' }}
                  >
                    <h6 className="mb-0 fw-semibold" style={{ color: '#1e293b' }}>
                      Guest Information
                    </h6>
                  </div>
                  <div className="card-body px-4 py-4">
                    {/* Quick Search for Existing Guest */}
                    <div className="mb-4">
                      <label className="form-label fw-medium text-muted mb-2">
                        Search Existing Guest
                      </label>
                      <AsyncSelect
                        cacheOptions
                        loadOptions={loadGuestOptions}
                        value={selectedGuestOption}
                        onChange={handleGuestSelect}
                        placeholder="Search by name, phone or email..."
                        isClearable
                        menuPortalTarget={document.body}
                        styles={portalStyles}
                        classNamePrefix="react-select"
                      />
                      <div className="form-text text-muted mt-1" style={{ fontSize: '0.8rem' }}>
                        Select an existing guest to auto-fill the information below (optional)
                      </div>
                    </div>
                    {/* Form Fields - Clean & Professional */}
                    <div className="row g-3">
                      {/* Required Fields */}
                      <div className="col-md-6">
                        <label className="form-label fw-medium required">
                          Full Name
                        </label>
                        <input
                          type="text"
                          name="customerName"
                          value={formValues.customerName}
                          onChange={handleInputChange}
                          className={`form-control ${errors.customerName ? 'is-invalid' : ''}`}
                          placeholder="Enter guest full name"
                        />
                        {errors.customerName && (
                          <div className="invalid-feedback">{errors.customerName}</div>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-medium required">
                          Phone Number
                        </label>
                        <PhoneInput
                          country="in"
                          value={formValues.phone?.replace?.(/^\+/, "") || ""}
                          onChange={handlePhoneChange}
                          enableSearch
                          containerStyle={{ width: "100%" }}
                          inputStyle={{
                            width: "100%",
                            height: "38px",
                            borderRadius: "6px",
                            borderColor: errors.phone ? '#dc3545' : '#ced4da',
                          }}
                          buttonStyle={{
                            borderRadius: "6px 0 0 6px",
                            borderColor: errors.phone ? '#dc3545' : '#ced4da',
                          }}
                        />
                        {errors.phone && (
                          <div className="invalid-feedback d-block">{errors.phone}</div>
                        )}
                      </div>
                      {/* Optional Fields */}
                      <div className="col-md-6">
                        <label className="form-label fw-medium">Email Address</label>
                        <input
                          type="email"
                          name="email"
                          value={formValues.email}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="guest@example.com"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-medium">Occasion</label>
                        <Select
                          options={occasionOptions}
                          value={getValue(occasionOptions, formValues.occasion)}
                          onChange={opt => handleSelectChange(opt, "occasion")}
                          placeholder="Select occasion (optional)"
                          isClearable
                          menuPortalTarget={document.body}
                          styles={portalStyles}
                          classNamePrefix="react-select"
                        />
                      </div>
                      {/* Source & Status */}
                      <div className="col-md-6">
                        <label className="form-label fw-medium required">Booking Source</label>
                        <Select
                          options={sourceOptions}
                          value={getValue(sourceOptions, formValues.source)}
                          onChange={opt => handleSelectChange(opt, "source")}
                          menuPortalTarget={document.body}
                          styles={portalStyles}
                          classNamePrefix="react-select"
                        />
                        {errors.source && (
                          <div className="invalid-feedback d-block">{errors.source}</div>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-medium required">Status</label>
                        <Select
                          options={statusOptions}
                          value={getValue(statusOptions, formValues.status)}
                          onChange={opt => handleSelectChange(opt, "status")}
                          menuPortalTarget={document.body}
                          styles={portalStyles}
                          classNamePrefix="react-select"
                        />
                        {errors.status && (
                          <div className="invalid-feedback d-block">{errors.status}</div>
                        )}
                      </div>
                      {/* Notes - Full width */}
                      <div className="col-12">
                        <label className="form-label fw-medium">Special Notes / Requests</label>
                        <textarea
                          name="notes"
                          value={formValues.notes}
                          onChange={handleInputChange}
                          className="form-control"
                          rows={3}
                          placeholder="Allergies, seating preferences, special requests, birthday celebration, etc..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* Footer */}
          <div className="ns-res-form-footer">
            <button
              type="button"
              className="ns-res-form-btn secondary"
              onClick={handleClose}
            >
              Cancel
            </button>
            <div className="d-flex gap-2">
              {step === 2 && (
                <button
                  type="button"
                  className="ns-res-form-btn secondary"
                  onClick={() => setStep(1)}
                >
                  Back
                </button>
              )}
              {step === 1 ? (
                <button
                  type="button"
                  className="ns-res-form-btn primary"
                  onClick={() => {
                    if (validateStep1()) setStep(2);
                  }}
                >
                  Continue
                </button>
              ) : (
                <button
                  type="button"
                  className="ns-res-form-btn primary"
                  onClick={doFinalSubmit}
                >
                  {initial ? "Update Reservation" : "Create Reservation"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Global Styles */}
      <style>{`
        body.ns-res-form-open {
          overflow: hidden !important;
        }

        .ns-res-form-root {
          position: fixed;
          inset: 0;
          z-index: 2147483000;
          display: flex;
          justify-content: flex-end;
          background: rgba(15, 23, 42, 0.08);
          backdrop-filter: blur(1.5px);
          -webkit-backdrop-filter: blur(1.5px);
        }

        .ns-res-form-backdrop {
          position: absolute;
          inset: 0;
          border: 0;
          background: transparent;
          padding: 0;
          cursor: pointer;
        }

        .ns-res-form-drawer {
          position: fixed;
          top: 0;
          right: 0;
          bottom: 0;
          width: min(920px, 100vw);
          height: 100vh;
          height: 100dvh;
          z-index: 2147483001;
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.12), transparent 34%),
            #f8fafc;
          box-shadow: -18px 0 52px rgba(15, 23, 42, 0.16);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .ns-res-form-content {
          height: 100%;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .ns-res-form-header {
          flex: 0 0 auto;
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.14), transparent 36%),
            linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
          border-bottom: 1px solid rgba(15, 23, 42, 0.08);
          padding: 18px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 14px;
        }

        .ns-res-form-title-wrap {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          min-width: 0;
        }

        .ns-res-form-icon {
          width: 48px;
          height: 48px;
          border-radius: 18px;
          background: linear-gradient(135deg, #1d4ed8, #2563eb);
          color: #ffffff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 14px 30px rgba(37, 99, 235, 0.22);
          font-weight: 950;
          flex: 0 0 auto;
        }

        .ns-res-form-eyebrow {
          color: #2563eb;
          font-size: 0.68rem;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          font-weight: 950;
          margin-bottom: 5px;
        }

        .ns-res-form-header h4 {
          color: #001f5b;
          font-size: 1.24rem;
          line-height: 1.14;
          font-weight: 950;
          margin: 0;
        }

        .ns-res-form-header p {
          color: #64748b;
          font-size: 0.78rem;
          line-height: 1.42;
          font-weight: 760;
          margin: 5px 0 0;
        }

        .ns-res-form-close {
          width: 40px;
          height: 40px;
          border-radius: 14px;
          border: 1px solid rgba(220, 38, 38, 0.16);
          background: #fff1f2;
          color: #dc2626;
          cursor: pointer;
          font-size: 1.35rem;
          line-height: 1;
          flex: 0 0 auto;
        }

        .ns-res-form-body {
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          padding: 16px 18px 112px;
          background: #f8fafc;
          scrollbar-width: thin;
          scrollbar-color: #93c5fd #e2e8f0;
        }

        .ns-res-form-card,
        .step-2-content .card {
          background: #ffffff !important;
          border: 1px solid rgba(15, 23, 42, 0.08) !important;
          border-radius: 20px !important;
          box-shadow: 0 10px 26px rgba(15, 23, 42, 0.055) !important;
          padding: 15px;
        }

        .ns-res-form-card .form-label,
        .step-2-content .form-label {
          color: #0f172a;
          font-size: 0.77rem;
          font-weight: 950 !important;
          margin-bottom: 7px;
        }

        .ns-res-form-card .form-control,
        .step-2-content .form-control {
          min-height: 44px;
          border-radius: 14px;
          border: 1px solid rgba(15, 23, 42, 0.12);
          font-weight: 760;
        }

        .ns-res-form-footer {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.96);
          border-top: 1px solid rgba(15, 23, 42, 0.08);
          box-shadow: 0 -14px 34px rgba(15, 23, 42, 0.08);
          padding: 13px 18px;
          display: flex;
          justify-content: space-between;
          gap: 10px;
          z-index: 4;
        }

        .ns-res-form-btn {
          min-height: 42px;
          border-radius: 14px;
          padding: 0 18px;
          border: 1px solid rgba(15, 23, 42, 0.10);
          font-size: 0.82rem;
          font-weight: 950;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          white-space: nowrap;
        }

        .ns-res-form-btn.secondary {
          background: #ffffff;
          color: #334155;
        }

        .ns-res-form-btn.primary {
          min-width: 150px;
          background: linear-gradient(135deg, #1d4ed8, #2563eb);
          border-color: #1d4ed8;
          color: #ffffff;
          box-shadow: 0 14px 30px rgba(37, 99, 235, 0.18);
        }

        .day-pill {
          transition: all 0.18s ease;
          border: 1px solid rgba(15, 23, 42, 0.08) !important;
          border-radius: 16px !important;
          background: #f8fafc !important;
          color: #334155 !important;
          font-weight: 850;
        }

        .day-pill.btn-primary,
        .day-pill.selected {
          background: linear-gradient(135deg, #1d4ed8, #2563eb) !important;
          color: #ffffff !important;
          border-color: #1d4ed8 !important;
          box-shadow: 0 12px 24px rgba(37, 99, 235, 0.18);
        }

        .day-pill:hover:not(.disabled):not(.selected) {
          border-color: #2563eb !important;
          color: #1d4ed8 !important;
          transform: translateY(-1px);
        }

        .day-pill.disabled {
          opacity: 0.48;
          cursor: not-allowed;
        }

        .time-slot {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 16px !important;
          transition: all 0.18s ease;
          min-height: 76px;
          color: #0f172a;
        }

        .time-slot:hover:not(.disabled):not(.selected) {
          background: #eff6ff;
          border-color: #2563eb;
          transform: translateY(-1px);
        }

        .time-slot.selected {
          background: linear-gradient(135deg, #1d4ed8, #2563eb);
          color: #ffffff !important;
          border-color: #1d4ed8;
          box-shadow: 0 12px 24px rgba(37, 99, 235, 0.18);
        }

        .time-slot.selected .badge {
          background-color: #ffffff !important;
          color: #1d4ed8 !important;
        }

        .time-slot.disabled {
          background: #f1f5f9;
          color: #94a3b8;
          cursor: not-allowed;
        }

        .react-select__control {
          min-height: 44px !important;
          border-radius: 14px !important;
          border-color: rgba(15, 23, 42, 0.12) !important;
          font-weight: 760;
        }

        .react-select__control--is-focused {
          border-color: #2563eb !important;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12) !important;
        }

        .react-tel-input .form-control {
          min-height: 44px !important;
          border-radius: 14px !important;
          width: 100% !important;
          font-weight: 760;
        }

        .react-tel-input .flag-dropdown {
          border-radius: 14px 0 0 14px !important;
        }

        .invalid-feedback,
        .text-danger {
          font-size: 0.76rem;
          font-weight: 800;
        }

        @media (max-width: 768px) {
          .ns-res-form-drawer {
            width: 100vw;
          }

          .ns-res-form-footer {
            display: grid;
            grid-template-columns: 1fr;
          }

          .ns-res-form-footer > div {
            width: 100%;
            display: grid !important;
            grid-template-columns: 1fr;
          }

          .ns-res-form-btn {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default ReservationFormModal;